/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type {Transformer} from './transformer';
import {CatalogApi} from "@backstage/plugin-catalog-react";
import {RepositoryEntityV1alpha1} from "../../../../../../backend/src/custom-processors/types";
import {getRepositoryEntity} from "../../../entity/utils/getRepositoryEntity";
import {Entity} from "@backstage/catalog-model";
import {DocPathInfo} from "@weave/schema-gen-ts/dist/schemas/docs-generator";

export const rewriteDocLinks = (catalogApiClient: CatalogApi, entity: Entity): Transformer => {
  return dom => {
    const updateDom = <T extends Element>(
      list: Array<T>
    ): void => {
      Array.from(list)
        .filter(elem => elem.hasAttribute('href'))
        .forEach((elem: T) => {
          let href = elem.getAttribute('href');
          if (href === "" || !href) {
            return;
          }

          getSwitchboardUrl(elem, catalogApiClient, entity, href).then(url => {
            // open external links in a new tab
            if (url.startsWith('http') && !url.startsWith(window.location.origin)) {
              elem.setAttribute('target', '_blank');
            }

            updateElementUrl(elem, url);
          })
        });
    };

    const updateElementUrl = (elem: Element, url: string): void => {
      try {
        const normalizedWindowLocation = normalizeUrl(
          window.location.href,
        );
        elem.setAttribute(
          'href',
          new URL(url, normalizedWindowLocation).toString(),
        );
      } catch (_e) {
        // Non-parseable links should be re-written as plain text.
        elem.replaceWith(elem.textContent || url);
      }
    }

    // Ex: <a href="https://docs.weavelab.ninja/services/devx-reference-services/devx-reference-run">Run</a>
    updateDom(Array.from(dom.getElementsByTagName('a')));

    return dom;
  };
};

/**
 * Make sure the URL ends with a trailing slash
 * This is to ensure routing within TechDocs works properly
 * @param input
 */
export function normalizeUrl(input: string): string {
  const url = new URL(input);

  if (!url.pathname.endsWith('/') && !url.pathname.endsWith('.html')) {
    url.pathname += '/';
  }

  return url.toString();
}

/**
 * Get the equivalent switchboard URL for Weave Docs and GitHub URLs if possible
 *
 * @param elem
 * @param catalogApiClient
 * @param currentEntity
 * @param url
 */
async function getSwitchboardUrl(elem: Element, catalogApiClient: CatalogApi, currentEntity: Entity, url: string): Promise<string> {
  // if the URL is not a docs URL, return the URL as is
  if (shouldIgnoreUrl(url)) {
    return url;
  }

  // when an anchor tag has a class, it means that it was generated by TechDocs and should be left alone
  // this applies to things like nav links and headers
  if (elem.getAttribute('class')) {
    return url;
  }

  if (url.includes('docs.weavelab.ninja')) {
    return await translateWeaveDocsUrl(catalogApiClient, url);
  }

  // this applies to any relative URls that are present in the markdown files
  if (isRelativeUrl(url)) {
    return await translateRelativeGithubUrl(currentEntity, url);
  }

  // this applies to all github.com/weave-lab URLs
  // this means that the URL is a full URL containing 'github.com/weave-lab' or that it is a relative URL
  return await translateGithubUrl(catalogApiClient, url);
}

/**
 * Check if the URL should be ignored
 * This returns true if the URL should be left alone and not be rewritten
 *
 * @param url
 */
function shouldIgnoreUrl(url: string): boolean {
  // if the URL is not a GitHub or Weave Docs URL, return true
  if (url.startsWith("http") && !url.includes("github.com/weave-lab/") && !url.includes("docs.weavelab.ninja")) {
    return true
  }

  // if the URL includes the current origin or switchboard, return true
  // the reason to also include 'switchboard.weavelab.ninja' is to account for running locally
  return url.includes(window.location.origin) || url.includes("switchboard.weavelab.ninja");
}

/**
 * Translate the Weave docs URL to the switchboard URL
 *
 * @param catalogApiClient
 * @param docsUrl
 */
async function translateWeaveDocsUrl(catalogApiClient: CatalogApi, docsUrl: string): Promise<string> {
  let uri = docsUrl.replace('https://docs.weavelab.ninja/', '');
  if (uri.endsWith('/')) {
    uri = uri.slice(0, -1);
  }
  let parts = uri.split('/');

  // first check to see if the 3rd parameter is a "Child" doc type of the 2nd parameter
  // ex: https://docs.weavelab.ninja/services/devx-reference-services/devx-reference-run
  if (parts.length >= 3) {
    let entity = await getRepositoryEntity(catalogApiClient, parts[2]);
    if (entity?.spec?.docsMetadata?.sourceMetadata?.parent === parts[1]) {
      return `${window.location.origin}/catalog/default/component/${parts[2]}/docs/${parts.slice(3).join('/')}`
    }
  }

  let entity = await getRepositoryEntity(catalogApiClient, parts[1]);
  if (!entity?.spec?.docsMetadata) {
    return docsUrl;
  }

  return `${window.location.origin}/catalog/default/component/${parts[1]}/docs/${parts.slice(2).join('/')}`
}

/**
 * Translate the Github URL to the switchboard URL
 *
 * @param catalogApiClient
 * @param githubUrl
 */
async function translateGithubUrl(catalogApiClient: CatalogApi, githubUrl: string): Promise<string> {
  if (hasNonMarkdownCodeFileExtension(githubUrl)) {
    return githubUrl;
  }

  // if the url is only a hash or in-page hyperlink, return it as is
  if (onlyHash(githubUrl)) {
    return githubUrl
  }

  // get the repo name from the URL
  // this works for absolute URLs, but for relative URLs this repo name will be overridden
  // ex: github.com/weave-lab/bart/blob/main/README.md => repoName = 'bart' (CORRECT)
  // ex: docs/rancher-setup/README.md => repoName = 'docs' (INCORRECT)
  let repoName = getRepoNameFromUrl(githubUrl);
  let hash = getHashFromUrl(githubUrl);
  let uriAfterSlug = getUriWithoutHashFromUrlAfterSlug(githubUrl, hash);
  if (uriAfterSlug.endsWith('/')) {
    uriAfterSlug = uriAfterSlug.slice(0, -1);
  }

  if (uriIsNativeGithubRoute(uriAfterSlug)) {
    return githubUrl;
  }

  let entity = await getRepositoryEntity(catalogApiClient, repoName);
  if (!entity?.spec?.docsMetadata) {
    return githubUrl;
  }

  // if url points to the main README.md file, remove the 'blob/(main/master)/' from the uri as it gets routed to the main page
  if (uriAfterSlug === 'blob/main/README.md' || uriAfterSlug === 'blob/master/README.md') {
    uriAfterSlug = '';
  }

  // If url points to a specific file or directory, check if there is a rewrite configured in the entity\
  // It's possible that in the .weave.yaml, there is configuration to move markdown files to a different directory
  // In this case, we need to look up in the docsMetadata to see if there is a mapping for this specific file
  // Ex: https://github.com/weave-lab/radioactive-man/tree/main/internal/checks/required
  // Ex: https://github.com/weave-lab/radioactive-man/blob/main/internal/checks/required/README.md
  if (uriAfterSlug.startsWith('blob/') || uriAfterSlug.startsWith('tree/')) {
    let rewrite = getUrlRewriteFromEntity(entity, uriAfterSlug);
    if (!rewrite) {
      return githubUrl
    }

    uriAfterSlug = rewrite;
  }

  let docsUrl = `${window.location.origin}/catalog/default/component/${repoName}/docs/${uriAfterSlug}`;
  if (docsUrl.endsWith('/')) {
    docsUrl = docsUrl.slice(0, -1);
  }
  if (hash) {
    docsUrl += `#${hash}`;
  }

  return docsUrl;
}

/**
 * Check if the URI after the slug is a native Github route such as 'releases' or 'settings'
 * @param uriAfterSlug
 */
function uriIsNativeGithubRoute(uriAfterSlug: string): boolean {
  const uriParts = uriAfterSlug.split('/'); // ex: releases/latest
  const nativeRoutes: string[] = ['issues', 'pulls', 'actions', 'projects', 'wiki', 'security', 'insights', 'releases', 'settings', 'compare', 'commits', 'branches', 'tags', 'contributors', 'stargazers', 'watchers', 'network', 'graphs'];
  return nativeRoutes.includes(uriParts[0]); // ex: uriParts[0] == 'releases'
}

/**
 * Get the URL rewrite from the entity
 *
 * If the URL points to a specific file or directory, check if there is a rewrite configured in the entity
 * spec docsMetadata
 *
 * @param entity
 * @param uriAfterSlug
 */
function getUrlRewriteFromEntity(entity: RepositoryEntityV1alpha1, uriAfterSlug: string): string {
  let parts = uriAfterSlug.split('/');
  let uri = parts.slice(2).join('/');

  let pathInfo = entity.spec?.docsMetadata?.sourceMetadata?.paths?.find((path) => path.sourcePath === uri);
  if (!pathInfo) {
    pathInfo = entity.spec?.docsMetadata?.sourceMetadata?.paths?.find((path) => path.sourcePath === uri + '/README.md');
    if (!pathInfo) {
      return ""
    }
  }

  if (!pathInfo.destinationPath) {
    return "";
  }

  let rewrite = pathInfo.destinationPath;

  // strip the leading slash since this will be appended
  if (rewrite?.startsWith('/')) {
    rewrite = rewrite.slice(1);
  }

  // remove the trailing '/_index.md' from the rewrite as it gets routed to a specific page
  if (rewrite?.endsWith('/_index.md')) {
    rewrite = rewrite?.slice(0, -10)
  }

  // if url points to a markdown file, remove the '.md' from the uri as it gets routed to a specific page
  // also, make it lowercase so it matches the routing setup in the docs-generator: https://github.com/weave-lab/docs-generator/commit/ff61f16b5cbf95ab838c48d51b14905128ab5999
  if (rewrite?.endsWith('.md')) {
    rewrite = rewrite?.slice(0, -3).toLowerCase()
  }

  let rewriteParts = rewrite.split('/');
  let encodedRewriteParts: string[] = [];
  rewriteParts.forEach((part) => {
    encodedRewriteParts.push(encodeURIComponent(part));
  })

  // return the encoded rewrite to handle special characters and spaces
  return encodedRewriteParts.join('/');
}

/**
 * Get the repo name from the URL
 *
 * Ex: https://github.com/weave-lab/schema/blob/main/README.md#authentication
 * Ex: https://github.com/weave-lab/schema/README.md#authentication
 * - both would return 'schema'
 * */
function getRepoNameFromUrl(url: string): string {
    let uri = url.replace('https://github.com/weave-lab/', '');
    let parts: string[] = uri.split('/');
    let repoName = parts[0];
    if (repoName.includes("#")) {
      let repoParts = repoName.split("#");
      repoName = repoParts[0];
    }
    return repoName;
}

/**
 * Get the hash from the URL
 *
 * Ex: https://github.com/weave-lab/schema/blob/main/README.md#authentication
 * Ex: https://github.com/weave-lab/schema/README.md#authentication
 * - both would return 'authentication'
 * */
function getHashFromUrl(url: string): string {
  let parts = url.split('/');
  let lastArg = parts[parts.length - 1];
  let hash = '';
  if (lastArg.includes("#")) {
    let lastArgParts = lastArg.split("#");
    hash = lastArgParts[1];
  }

  return hash.replace('--', '-');
}

/**
 * Get the URI after the slug and remove the hash
 *
 * Ex: https://github.com/weave-lab/schema/blob/main/README.md#authentication
 * - would return 'blob/main/README.md'
 * */
function getUriWithoutHashFromUrlAfterSlug(url: string, hash: string): string {
  let uri = url.replace('https://github.com/weave-lab/', '');
  let parts = uri.split('/');
  return parts.slice(1).join('/').replace(`#${hash}`, '');
}

/**
 * Check if the URL has a file extension that is not markdown
 *
 * Ex: https://github.com/weave-lab/schema/blob/main/server.go
 * - would return 'false'
 * */
function hasNonMarkdownCodeFileExtension(url: string): boolean {
  let parts = url.split('/');
  let lastArg = parts[parts.length - 1];
  if (lastArg.includes('#')) {
    lastArg = lastArg.split('#')[0];
  }

  return lastArg.includes('.') && !lastArg.endsWith('.md');
}

/**
 * Check if the url is only a hash, or in-page hyperlink
 *
 * @param url
 */
function onlyHash(url: string): boolean {
  return !url.includes("/") && url.startsWith("#")
}

/**
 * Checks if a URL points to a file with a Markdown extension.
 *
 * Works with only .md extension
 *
 * @param url - The URL string to test
 * @returns true if the URL has a markdown extension, false otherwise
 */
export function hasMarkdownExtension(url: string): boolean {
  // Strip query parameters or hash fragments
  const cleanUrl = url.split(/[?#]/)[0];
  const lastSegment = cleanUrl.split('/').pop() || '';
  return /\.(md)$/i.test(lastSegment);
}

/**
 * Returns true if the given URL is a relative URL.
 * This excludes URLs starting with http, https, mailto, / (absolute), etc.
 * 
 * @param url - The URL to check.
 */
function isRelativeUrl(url: string): boolean {
  return !/^(?:[a-zA-Z][a-zA-Z\d+\-.]*:|\/)/.test(url);
}

/**
 * Removes the `.md` extension from a URL or path, preserving query/hash.
 * @param url - The URL or path to process.
 */
function stripMdExtension(url?: string): string {
  return url ? url.replace(/\.md(?=[?#]|$)/, '') : '';
}

/**
 * Translate the relative github URLs to the switchboard URL
 * @param currentEntity
 * @param url
 */
async function translateRelativeGithubUrl(currentEntity: Entity, url: string): Promise<string> {
  // For small case markdown file references the relative urls are already rewritten by
  // techdocs-cli, so they don't have a .md extension anymore hence we return it as is
  if (!hasMarkdownExtension(url)) {
    return url;
  }
  /*
   * We construct a dummy base URL using the current docs path to resolve the relative URL correctly.
   * This ensures that relative paths are interpreted in the context of the current document's location.
   *
   * Example:
   * Current URL: http://localhost:3000/catalog/default/component/devx-knowledgebase/docs/canary-deploys/introduction/
   * Current Docs Path: canary-deploys/introduction (we remove the trailing slash so that URL resolution works relative to parent directory of current document)
   * Dummy Base URL: http://dummy/canary-deploys/introduction
   * Relative URL: ../bart/setup.md
   * Resolved Path: bart/setup.md ( we remove the leading slash to make it relative to the root of the docs)
   */
  const pathname = window.location.pathname; 
  let currentDocsPath = '';
  const docsIndex = pathname.indexOf('/docs');
  if (docsIndex !== -1) {
    currentDocsPath = pathname.slice(docsIndex + '/docs'.length);
    if (currentDocsPath.startsWith('/')) {
      currentDocsPath = currentDocsPath.slice(1);
    }
  }
  const dummyBaseUrl = `http://dummy/${currentDocsPath ? currentDocsPath : ''}`;
  let resolvedPath = new URL(url, dummyBaseUrl).pathname.substring(1);

  const repoEntity = currentEntity as RepositoryEntityV1alpha1;
  resolvedPath = repoEntity.spec.docsMetadata?.sourceMetadata?.paths?.find((path: DocPathInfo) => path.sourcePath === resolvedPath)?.destinationPath || resolvedPath;
  resolvedPath = resolvedPath.toLowerCase();

  // In case the resolved path points to a README.md file, we need to remove it due to how routing is configured 
  if (resolvedPath.endsWith('readme.md')) {
    resolvedPath = resolvedPath.replace(/\/?readme\.md$/i, '');
  } else {
    // Remove .md extension for other files
    resolvedPath = stripMdExtension(resolvedPath);
  }

  // switchboard URL resolved relative to the current url
  return `${window.location.origin}/catalog/default/component/${currentEntity.metadata.name}/docs/${resolvedPath}`;
}
